import { useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { t, type MessageKey } from '../i18n/index.js';
import { ApiError, post } from './api.js';
import {
  fmtDateShort,
  fmtDateTime,
  fmtMonthYear,
  inviteShareMessage,
  whatsappShareUrl,
  type PasskeyHome,
  type Shift,
} from '@siva/shared';
import { t as tr } from '../i18n/index.js';

export function Spinner() {
  return (
    <div className="skeleton" aria-busy="true" aria-label={t('loading')}>
      <span className="skeleton-line" style={{ width: '92%' }} />
      <span className="skeleton-line" style={{ width: '74%' }} />
      <span className="skeleton-line" style={{ width: '56%' }} />
    </div>
  );
}

export function Alert({
  kind = 'error',
  children,
}: {
  kind?: 'error' | 'success' | 'info' | 'warn';
  children: ReactNode;
}) {
  return (
    <div className={`alert ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/**
 * What a worker's passkey says about the device it lives on, in words.
 *
 * Shown so the roster can be looked at before anything is decided from it: an Apple passkey means
 * an employee with no app to install, which is the case the manual "cannot use the app" box
 * exists to cover. 'portable' and 'unknown' are deliberately not dressed up as answers.
 */
export function passkeyHomeLabel(home: PasskeyHome): string {
  const key: Record<PasskeyHome, MessageKey> = {
    apple: 'pk_home_apple',
    android: 'pk_home_android',
    windows: 'pk_home_windows',
    portable: 'pk_home_portable',
    unknown: 'pk_home_unknown',
  };
  return t(key[home]);
}

/** The class for that label: an Apple one is the case worth finding at a glance. */
export const passkeyHomeClass = (home: PasskeyHome): string =>
  home === 'apple' ? 'device-home is-apple' : 'device-home';

/**
 * "1st Shift" / "2nd Shift", through the message table rather than from the enum.
 *
 * A Record keyed by Shift rather than a template key, so adding a third shift is a type error
 * here instead of a missing string on screen.
 */
export function shiftLabel(s: Shift): string {
  const key: Record<Shift, 'shift_first' | 'shift_second'> = {
    first: 'shift_first',
    second: 'shift_second',
  };
  return t(key[s]);
}

/** "11 Sep 2026, 09:02" or, without time, "11 Sep 2026" (office timezone; see shared/format). */
export function fmtDate(iso: string | null | undefined, withTime = true): string {
  return withTime ? fmtDateTime(iso) : fmtDateShort(iso);
}

export type InviteLink = {
  id?: string;
  url: string;
  qr: string;
  purpose: 'onboard' | 'reregister' | 'admin_enroll' | string;
  expires_at: string;
  recipient?: { id: string; name: string; first_name: string | null; phone: string | null };
};

/** QR + copyable URL for a freshly created invite/enrolment link. Shown once; the server keeps only a hash. */
export function InviteLinkCard({
  link,
  note,
  onClose,
}: {
  link: InviteLink;
  note?: string;
  onClose?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const title =
    link.purpose === 'admin_enroll'
      ? t('link_enrol_title')
      : link.purpose === 'reregister'
        ? t('link_reregister_title')
        : t('link_title');
  /** One audit row per share; best effort so a failed audit call never blocks the admin. */
  const recordShare = (channel: 'copy' | 'whatsapp') => {
    if (!link.id) return;
    void post(`/admin/invites/${link.id}/share`, { channel }).catch(() => {});
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable: user can select the text */
    }
    recordShare('copy');
  };
  const waText = inviteShareMessage(link.recipient?.first_name, link.url, link.purpose);
  const waUrl = whatsappShareUrl(link.recipient?.phone, waText);
  return (
    <section className="card invite-card">
      <h3>{title}</h3>
      {note && <Alert kind="info">{note}</Alert>}
      <img src={link.qr} alt="QR code" className="qr" width={240} height={240} />
      <p className="url">
        <code>{link.url}</code>
      </p>
      <div className="row">
        <button type="button" onClick={copy}>
          {copied ? t('copied') : t('copy_link')}
        </button>
        {waUrl && (
          <a
            className="button whatsapp"
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => recordShare('whatsapp')}
          >
            {t('share_whatsapp')}
          </a>
        )}
        {onClose && (
          <button type="button" className="secondary" onClick={onClose}>
            {t('close')}
          </button>
        )}
      </div>
      <p className="muted">{t('link_expires', { date: fmtDate(link.expires_at) })}</p>
      <p className="muted">{t('link_shown_once')}</p>
    </section>
  );
}

/** Message for a 409 phone_taken error, naming the other account when the API says who. */
export function phoneTakenMessage(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== 'phone_taken') return null;
  const name = err.details.used_by;
  return typeof name === 'string' && name
    ? tr('worker_phone_taken_by', { name })
    : tr('worker_phone_taken');
}

/** Inline error under a form field. Renders nothing when there is no message. */
/**
 * A password field with an eye. Twelve characters typed blind on a phone keyboard are wrong
 * often enough that the owner asked to see them; the eye is a toggle, off by default, and the
 * input is otherwise the plain one it replaces (every attribute passes through).
 *
 * The label is a sibling, not a wrapper: a button inside a <label> joins the field's accessible
 * name and answers to the label's text itself, and "Password" then found two elements.
 */
export function PasswordField({
  id,
  label,
  hint,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'> & {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div className="field password-field">
      <label htmlFor={id}>
        {label}
        {hint && <> {hint}</>}
      </label>
      <span className="password-wrap">
        <input {...props} id={id} type={shown ? 'text' : 'password'} />
        <button
          type="button"
          className="password-eye"
          aria-label={shown ? t('password_hide') : t('password_show')}
          aria-pressed={shown}
          onClick={() => setShown((s) => !s)}
        >
          {shown ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
        </button>
      </span>
    </div>
  );
}

export function FieldError({ id, message }: { id: string; message: string | null | undefined }) {
  if (!message) return null;
  return (
    <span id={id} className="field-error" role="alert">
      {message}
    </span>
  );
}

/** "2026-09" → "September 2026". */
export const fmtMonth = fmtMonthYear;
